import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { pillarApi, pillarErr, label, pillarDot } from "@/lib/pillarApi";
import { labelCls, Pill, Empty } from "@/components/nds/windows/resident/ui";
import { AssignmentCard, MagicLink, NewAssignment } from "./parts";
import { LifePassPin } from "./LifePassPin";
import { LessonResults } from "./LessonResults";
import { PillarProgress, STATUS_TONE } from "./PillarProgress";
import { BookOpen, ClipboardList, Handshake, TrendingUp } from "lucide-react";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";

function AssignmentsTab({ resident, onChanged }) {
  const { summary, pillar } = resident;
  const rows = summary.assignments.filter((a) => a.pillar_id === pillar.id);
  const hwById = Object.fromEntries(summary.homework.map((h) => [h.id, h]));
  const hasOpen = rows.some((a) => a.status !== "staff_assessed");
  return (
    <div data-testid="pillar-tab-assignments-panel" className="space-y-3">
      {hasOpen ? <p data-testid="pillar-new-blocked" className="text-xs text-nex-muted">An open task exists for this pillar. Verify it before assigning the next week.</p>
        : <NewAssignment residentId={resident.id} pillarId={pillar.id} onCreated={onChanged} />}
      {!rows.length ? <Empty testId="pillar-assignments-empty">No assignments for this pillar yet.</Empty> : (
        <ul className="space-y-2">{rows.map((a) => <AssignmentCard key={a.id} a={a} hw={hwById[a.homework_task_id]} onChanged={onChanged} />)}</ul>
      )}
    </div>
  );
}

function HomeworkTab({ resident }) {
  const { summary, pillar } = resident;
  const rows = summary.homework.filter((h) => h.pillar_id === pillar.id);
  const current = rows.find((h) => !["verified", "waived"].includes(h.status));
  return (
    <div data-testid="pillar-tab-homework-panel" className="space-y-3">
      <div data-testid="pillar-current-homework" className="p-3 border border-nex-gold/30 rounded-sm bg-nex-gold/5">
        <div className={labelCls}>Current homework</div>
        {current ? (
          <div>
            <div className="text-sm text-nex-silver">{current.title} <Pill tone={STATUS_TONE[current.status]}>{label(current.status)}</Pill></div>
            {current.steps?.length > 0 && <ol className="mt-1 list-decimal ml-5 text-xs text-nex-silver/80 space-y-0.5">{current.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>}
            <div className="mt-1 text-[11px] text-nex-muted">Due {current.due_at ? new Date(current.due_at).toLocaleDateString() : "—"}</div>
          </div>
        ) : <p className="text-sm text-nex-muted">No open homework for this pillar.</p>}
      </div>
      {rows.length > 0 && (
        <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">
          {rows.map((h) => (
            <li key={h.id} data-testid={`pillar-homework-${h.id}`} className="px-3 py-2 flex items-center gap-2 text-sm">
              <Pill tone="dim">Wk {h.program_week}</Pill>
              <span className="flex-1 truncate text-nex-silver">{h.title}</span>
              <Pill tone={STATUS_TONE[h.status]}>{label(h.status)}</Pill>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgressTab({ resident }) {
  const st = resident.summary.participant_state;
  return (
    <div data-testid="pillar-tab-progress-panel" className="space-y-4">
      <div className="flex flex-wrap gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-nex-muted">
        <span data-testid="pillar-state-day">Day {st.program_day_current}</span>·
        <span data-testid="pillar-state-week">Expected week {st.expected_week}</span>·
        <span data-testid="pillar-state-pct">{st.completion_pct}% complete</span>·
        <span data-testid="pillar-state-pace" className={st.pace_status === "BEHIND" ? "text-red-300" : "text-emerald-300"}>{label(st.pace_status)}</span>
      </div>
      <PillarProgress pillars={resident.pillars} state={st} />
      <MagicLink residentId={resident.id} />
      <LifePassPin residentId={resident.id} />
      <LessonResults residentId={resident.id} />
    </div>
  );
}

function TouchpointsTab({ resident }) {
  const { summary, pillar } = resident;
  const rows = (summary.touchpoints || []).filter((t) => t.pillar_id === pillar.id);
  if (!rows.length) return <Empty testId="pillar-touchpoints-empty">No touchpoint sessions for this pillar.</Empty>;
  return (
    <ul data-testid="pillar-tab-touchpoints-panel" className="space-y-2">
      {rows.map((t) => (
        <li key={t.id} data-testid={`pillar-touchpoint-${t.id}`} className="p-3 border border-white/10 rounded-sm space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-nex-muted">
            <Pill tone="dim">Wk {t.program_week}</Pill>
            <span>{t.started_at ? new Date(t.started_at).toLocaleString() : "—"}</span>
            <span>· {t.conducted_by?.name || "—"}</span>
            {t.any_urgent && <Pill tone="red">Urgent</Pill>}
            {t.legacy_import && <Pill tone="dim">Old platform</Pill>}
          </div>
          {t.queue.map((q, i) => (
            <div key={i} className="text-sm space-y-0.5">
              <div className="text-nex-silver">{q.question_text || "Question"}</div>
              {q.selected_letter && <div className="text-xs text-nex-chrome">Answer {q.selected_letter}{q.selected_text ? ` · ${q.selected_text}` : ""}</div>}
              {q.notes && <div className="text-xs text-nex-muted">Staff note: {q.notes}</div>}
            </div>
          ))}
          {t.notes && <p className="text-xs text-nex-muted">{t.notes}</p>}
        </li>
      ))}
    </ul>
  );
}

const TABS = [
  ["assignments", "Assignments", AssignmentsTab],
  ["homework", "Homework", HomeworkTab],
  ["touchpoints", "Touchpoints", TouchpointsTab],
  ["progress", "Progress", ProgressTab],
];
const TAB_KEY = "tea_pillar_detail_tab";

export default function PillarResidentDetail({ residentId, residentName, pillar, pillars, onBack, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const ct = useCardTabs(localStorage.getItem(TAB_KEY) || "assignments", (id) => { try { localStorage.setItem(TAB_KEY, id); } catch { /* storage unavailable */ } });
  const tab = ct.tab;
  const load = () => pillarApi.get(`/residents/${residentId}/pillar-summary`).then(setSummary).catch((e) => setError(pillarErr(e)));
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = ct.open;
  const refresh = () => { load(); onChanged?.(); };
  const Active = (TABS.find(([id]) => id === tab) || TABS[0])[2];
  const resident = summary && { id: residentId, name: residentName, pillar, pillars, summary };
  const mine = (xs) => (xs || []).filter((x) => x.pillar_id === pillar.id);
  const st = summary?.participant_state;
  const hwOpen = mine(summary?.homework).filter((h) => h.status !== "verified").length;
  const cards = [
    { id: "assignments", icon: ClipboardList, tone: "ops", desc: "Assigned questions for this pillar and the resident's answers", stat: summary ? mine(summary.assignments).length : null, note: "assignments" },
    { id: "homework", icon: BookOpen, tone: "data", desc: "Homework tasks, submissions and verification", stat: summary ? hwOpen : null, note: "open homework", alert: hwOpen > 0 },
    { id: "touchpoints", icon: Handshake, tone: "comms", desc: "Touchpoint sessions logged with the resident", stat: summary ? mine(summary.touchpoints).length : null, note: "touchpoints" },
    { id: "progress", icon: TrendingUp, tone: "financial", desc: "Pace, expected week, PACT Life Pass PIN and lesson results", stat: st ? `${st.completion_pct}%` : null, note: st ? `day ${st.program_day_current} · ${label(st.pace_status)}` : "", alert: st?.pace_status === "BEHIND" },
  ].map((c) => ({ ...c, label: TABS.find(([id]) => id === c.id)[1] }));
  return (
    <div data-testid="pillar-resident-detail" className="flex flex-col h-full">
      <div className="px-5 pt-3 pb-3 border-b border-white/10">
        {onBack && <button type="button" onClick={onBack} data-testid="pillar-detail-back"
          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold hover:text-nex-goldbright">
          <ArrowLeft className="h-3.5 w-3.5" /> All pillars
        </button>}
        <div className="mt-2 flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: pillarDot(pillar.color_hex) }} />
          <h3 data-testid="pillar-detail-name" className="text-lg font-semibold text-nex-silver">{residentName}</h3>
          <span className="text-xs text-nex-muted">· {pillar.name}</span>
        </div>
        {summary?.pillar_leads?.[pillar.id] && (
          <div data-testid="pillar-detail-lead" className="mt-1 text-xs text-nex-chrome">
            Pillar lead: <span className="text-nex-silver">{summary.pillar_leads[pillar.id].name}</span>
            {summary.pillar_leads[pillar.id].email && <span className="text-nex-muted"> · {summary.pillar_leads[pillar.id].email}</span>}
          </div>
        )}
        {!ct.compact && <div role="tablist" className="mt-3 flex gap-1">
          {TABS.map(([id, text]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => pick(id)} data-testid={`pillar-tab-${id}`}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] uppercase border-b-2 transition-colors ${
                tab === id ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>{text}</button>
          ))}
        </div>}
      </div>
      <div ref={ct.ref} className="flex-1 overflow-y-auto p-5">
        {error ? <p data-testid="pillar-detail-error" className="text-red-300 text-sm">{error}</p>
          : !resident ? <div className="flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : ct.deck ? <CardDeck testid="pillar-cards" items={cards} onOpen={pick} />
          : <>{ct.compact && <CardsBack testid="pillar-cards-back" label={(TABS.find(([id]) => id === tab) || TABS[0])[1]} onClick={ct.close} />}<Active resident={resident} onChanged={refresh} /></>}
      </div>
    </div>
  );
}
